import React, { useState, useEffect } from 'react';
import { Clock, Coffee, PauseCircle, PlayCircle, FileText, X } from 'lucide-react';
import { useToast } from './Toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TimeClockModal = ({ assistantId, onClose }) => {
  const [timeData, setTimeData] = useState(null);
  const [weekTotal, setWeekTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const { success, error } = useToast();

  useEffect(() => {
    fetchTimeData();
    fetchWeekTotal();
    
    // Refresh every minute
    const interval = setInterval(() => {
      fetchTimeData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [assistantId]);

  const fetchTimeData = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/time/today`, {
        headers: { 'x-assistant-session': assistantId }
      });
      const data = await res.json();
      if (data.ok) {
        setTimeData(data);
      }
    } catch (err) {
      console.error('Failed to fetch time data:', err);
    }
  };

  const fetchWeekTotal = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/time/week`, {
        headers: { 'x-assistant-session': assistantId }
      });
      const data = await res.json();
      if (data.ok) {
        setWeekTotal(data.total_hours);
      }
    } catch (err) {
      console.error('Failed to fetch week total:', err);
    }
  };

  const handleClockIn = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/time/clock-in`, {
        method: 'POST',
        headers: { 'x-assistant-session': assistantId }
      });
      const data = await res.json();
      
      if (data.ok) {
        success('Clocked In', 'Your work time is now being tracked');
        fetchTimeData();
      } else {
        error('Error', data.message || 'Failed to clock in');
      }
    } catch (err) {
      error('Network Error', 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/time/clock-out`, {
        method: 'POST',
        headers: { 'x-assistant-session': assistantId }
      });
      const data = await res.json();
      
      if (data.ok) {
        success('Clocked Out', `Total hours: ${data.hours_worked}`);
        fetchTimeData();
        fetchWeekTotal();
      } else {
        error('Error', data.message || 'Failed to clock out');
      }
    } catch (err) {
      error('Network Error', 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleBreakStart = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/time/break-start`, {
        method: 'POST',
        headers: { 'x-assistant-session': assistantId }
      });
      const data = await res.json();
      
      if (data.ok) {
        success('Break Started', 'Taking a break');
        fetchTimeData();
      } else {
        error('Error', data.message);
      }
    } catch (err) {
      error('Network Error', 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleBreakEnd = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/time/break-end`, {
        method: 'POST',
        headers: { 'x-assistant-session': assistantId }
      });
      const data = await res.json();
      
      if (data.ok) {
        success('Break Ended', 'Back to work');
        fetchTimeData();
      } else {
        error('Error', data.message);
      }
    } catch (err) {
      error('Network Error', 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const isClockedIn = timeData?.clocked_in;
  const isOnBreak = timeData?.on_break;
  const todayHours = timeData?.entry?.current_hours || timeData?.entry?.total_hours || 0;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          background: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          width: '380px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
          zIndex: 9999
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={20} style={{ color: '#3B82F6' }} />
            <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: 0 }}>
              Time Clock
            </h3>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isClockedIn && (
              <div style={{
                padding: '4px 12px',
                borderRadius: '20px',
                background: isOnBreak ? '#FEF3C7' : '#DCFCE7',
                fontSize: '12px',
                fontWeight: '600',
                color: isOnBreak ? '#92400E' : '#166534'
              }}>
                {isOnBreak ? '☕ On Break' : '✓ Clocked In'}
              </div>
            )}
            
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6B7280',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* Time Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '16px',
            padding: '16px',
            background: '#F9FAFB',
            borderRadius: '8px'
          }}>
            <div>
              <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 4px 0', fontWeight: '500' }}>
                Today
              </p>
              <p style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '700', margin: 0 }}>
                {formatHours(todayHours)}
              </p>
            </div>
            <div>
              <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 4px 0', fontWeight: '500' }}>
                This Week
              </p>
              <p style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '700', margin: 0 }}>
                {formatHours(weekTotal)}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {!isClockedIn ? (
              <button
                onClick={handleClockIn}
                disabled={loading}
                style={{
                  padding: '12px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <PlayCircle size={18} />
                Clock In
              </button>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button
                    onClick={handleClockOut}
                    disabled={loading}
                    style={{
                      padding: '12px',
                      background: '#DC2626',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    <PauseCircle size={16} />
                    Clock Out
                  </button>
                  
                  {!isOnBreak ? (
                    <button
                      onClick={handleBreakStart}
                      disabled={loading}
                      style={{
                        padding: '12px',
                        background: '#F59E0B',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      <Coffee size={16} />
                      Start Break
                    </button>
                  ) : (
                    <button
                      onClick={handleBreakEnd}
                      disabled={loading}
                      style={{
                        padding: '12px',
                        background: '#10B981',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      <PlayCircle size={16} />
                      End Break
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Manual Entry Button */}
            <button
              onClick={() => setShowManualEntry(true)}
              style={{
                padding: '10px',
                background: 'transparent',
                color: '#3B82F6',
                border: '1px solid #BFDBFE',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <FileText size={16} />
              Submit Manual Entry
            </button>
          </div>
        </div>
      </div>

      {/* Manual Entry Modal */}
      {showManualEntry && (
        <ManualEntryModal
          assistantId={assistantId}
          onClose={() => setShowManualEntry(false)}
          onSubmit={() => {
            setShowManualEntry(false);
            success('Submitted', 'Manual time entry submitted for approval');
          }}
        />
      )}
    </>
  );
};

// Manual Entry Modal Component
const ManualEntryModal = ({ assistantId, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { error } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reason.trim()) {
      error('Required Field', 'Please provide a reason for manual entry');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/time/manual-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-assistant-session': assistantId
        },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (data.ok) {
        onSubmit();
      } else {
        error('Error', data.message || 'Failed to submit entry');
      }
    } catch (err) {
      error('Network Error', 'Could not connect to server');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          maxWidth: '480px',
          width: '100%',
          padding: '24px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
          Submit Manual Time Entry
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Start Time
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                End Time
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
              Reason (Required)
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              required
              placeholder="Example: Forgot to clock in, worked off-platform, adjusted hours"
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                background: 'transparent',
                color: '#6B7280',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 1,
                padding: '12px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimeClockModal;
